import { ApiClient } from '@center-technologies/shared-api-clients';
import { invalidateSphereById } from './getSphereById';

interface UpdateUrgencyParams {
  edgeId: string;
  urgencyCode: string;
}

interface UpdateUrgencyResponse {
  newEdgeId: string;
}

interface UpdateUrgencyResult {
  success: boolean;
  statusCode: number;
  message: string;
  newEdgeId: string;
}

export class UpdateUrgencyError extends Error {
  success: boolean;
  statusCode: number;
  response: any;

  constructor(message: string, statusCode: number, response: any) {
    super(message);
    this.name = 'UpdateUrgencyError';
    this.success = false;
    this.statusCode = statusCode;
    this.response = response;
  }
}

/**
 * Updates the urgency for a given edge ID.
 *
 * @param apiClient - An ApiClient configured for your Graph endpoint
 * @param params.edgeId - The ID of the edge to update
 * @param params.urgencyCode - The urgency code to set (e.g., '1' for Low, '5' for Urgent)
 * @throws UpdateUrgencyError if the request fails
 * @returns A promise that resolves to the update result
 */
export async function updateUrgency(
  apiClient: ApiClient,
  params: UpdateUrgencyParams
): Promise<UpdateUrgencyResult> {
  const { edgeId, urgencyCode } = params;

  if (!edgeId?.trim() || !urgencyCode?.trim()) {
    throw new UpdateUrgencyError(
      'Invalid parameters: edgeId and urgencyCode are required',
      400,
      { status: 400, data: { error: 'Bad Request' } }
    );
  }

  console.log('updateUrgency:', edgeId, urgencyCode);

  try {
    // ApiClient.put automatically handles the Bearer token header
    const data = await apiClient.put<UpdateUrgencyResponse>(
      `/edges/${edgeId}/urgency`,
      { edgeId, urgency: urgencyCode }
    );

    // Urgency lives on an edge, so the affected spheres are not identifiable from
    // the parameters here — drop the whole full-sphere read cache rather than risk
    // handing back a vertex whose relationship data is now wrong. The cache's TTL is
    // seconds, so the cost is a refetch, not a stampede.
    invalidateSphereById();

    console.log('Urgency updated successfully:', data);
    
    return {
      success: true,
      statusCode: 200,
      message: 'Urgency updated successfully',
      newEdgeId: data.newEdgeId,
    };
  } catch (error: any) {
    if (error.response) {
      throw new UpdateUrgencyError(
        error.response.data?.error || 'Failed to update urgency',
        error.response.status,
        error.response
      );
    } else {
      throw new UpdateUrgencyError(
        error.message || 'Unknown error occurred while updating urgency',
        500,
        {
          status: 500,
          data: { error: 'Internal Server Error' },
        }
      );
    }
  }
}